import BankAccount from "entities/BankAccount";
import * as ibantools from "ibantools";
import { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Option } from "react-bootstrap-typeahead/types/types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Bank from "../../../../../entities/Bank";
import { AppDispatch, AppState } from "../../../../../store/configureStore";
import { getBanks } from "../../../../../store/slices/banks";
import ModalHeaderWithActions from "../../../../UI/ModalHeaderWithActions";
import TypeaheadWithIcon from "../../../../UI/TypeaheadWithIcon";
import "../ChangeDialogs.css";
import ProfileEditContentNode from "../ProfileEditContentNode";
import ProfileEditNode from "../ProfileEditNode";

interface BankOption {
    id: number;
    label: string;
    bank: Bank;
}
interface Props {
    currentBankAccount: BankAccount | undefined;
    show: boolean;
    onClose: () => void;
    onSubmit: (data: BankAccount) => void;
}

const ChangeBankAccount = (props: Props) => {

    const emptyAccount: BankAccount = {
        Id: 0,
        BankAccountCode: "",
        BankAccountId: 0,
        BankId: 0,
        BankName: "",
        IBAN: "",
        IsDefault: false,
        OwnerName: "",
        WorkerId: 0,
        PercentageOfSalary: 100
    };
    const [currentBankAccount, setCurrentBankAccount] = useState(props.currentBankAccount || emptyAccount);
    const { show, onClose, onSubmit } = props;
    useEffect(() => {
        if (props.show) {
            setCurrentBankAccount(props.currentBankAccount || emptyAccount);
        }
    }, [props.show]);

    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();

    const minDate = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);
    const { list: banks } = useSelector(
        (state: AppState) => state.entities.banks
    );

    useEffect(() => {
        if (show && banks.length === 0) {
            dispatch(getBanks());
        }
    }, [show, banks.length, dispatch]);

    const [selectedBank, setSelectedBank] = useState<Bank | undefined>(
        banks.find(bank => bank.Id === currentBankAccount?.BankId)
    );

    useEffect(() => {
        setSelectedBank(banks.find(bank => bank.Id === currentBankAccount?.BankId));
    }, [banks, currentBankAccount]);

    // Map banks to Typeahead-compatible format
    const options = banks.map(bank => ({
        id: bank.Id,
        label: bank.Name,
        bank,
    }));

    const handleBankChange = (selected: Option[]) => {
        const typedSelected = selected as BankOption[];
        if (typedSelected.length > 0) {
            setSelectedBank(typedSelected[0].bank);
        } else {
            setSelectedBank(undefined);
        }
    };

    // Initialize with empty values if currentBankAccount is null
    const initialFormData = {
        ownerName: currentBankAccount?.OwnerName || "",
        iban: currentBankAccount?.IBAN || "",
        percentageOfSalary:  100,
        startDate: currentBankAccount?.StartDate || minDate
    };

    const [formData, setFormData] = useState(initialFormData);

    const [validation, setValidation] = useState({
        percentageValid: true,
        ibanValid: true,
        touched: {
            iban: false,
            ownerName: false,
            percentage: false
        }
    });

    const handleChange = (field: keyof typeof formData, value: string | number | Date) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (field === 'iban') {
            const ibanValid = ibantools.isValidIBAN(value as string);
            setValidation(prev => ({
                ...prev,
                ibanValid: ibanValid
            }));
        }

        setValidation(prev => ({
            ...prev,
            touched: {
                ...prev.touched,
                [field]: true
            }
        }));

    };

    const canSubmit = useMemo(() => {

        return formData.ownerName.trim() !== "" &&
            validation.ibanValid &&
            selectedBank !== undefined;
    }, [validation, formData, selectedBank]);

    const handleSubmit = () => {

        if (!canSubmit || !formData) return;

        const bankDetails: BankAccount = {
            ...currentBankAccount,
            OwnerName: formData.ownerName,
            IBAN: formData.iban,
            StartDate: formData.startDate,
            PercentageOfSalary: formData.percentageOfSalary,
            BankId: selectedBank?.Id || 0,
            BankName: selectedBank?.Name || "",
            BankAccountCode: selectedBank?.Code || "",
        };

        onSubmit(bankDetails);
    };

    // Reset form when currentBankAccount changes
    useEffect(() => {
        setFormData({
            ownerName: currentBankAccount?.OwnerName || "",
            iban: currentBankAccount?.IBAN || "",
            percentageOfSalary: currentBankAccount?.PercentageOfSalary || 100,
            startDate: currentBankAccount?.StartDate || minDate
        });
    }, [currentBankAccount, minDate]);

    return show && (
        <Modal show={show} onHide={onClose} className="change-modal">
            <ModalHeaderWithActions
                title={t("changeDialog.bankAccount.changeBankAccount")}
                onClose={onClose}
                onSubmit={handleSubmit}
                canSubmit={canSubmit}
            />
            <Modal.Body>
                <Form>
                    <ProfileEditContentNode
                        label={t("pages:profile.bankName")}
                        hideClearButton={true}
                        handleClear={() => { }}
                    >
                        <TypeaheadWithIcon
                            id="formBankAccountBank"
                            onChange={handleBankChange}
                            options={options}
                            selected={selectedBank ? [{ id: selectedBank.Id, label: selectedBank.Name, bank: selectedBank }] : []}
                            placeholder={t("changeDialog.bankAccount.selectBankName")}
                            labelKey="label"
                            className="form-control-input-custom"
                        />
                    </ProfileEditContentNode>
                    <ProfileEditNode
                        controlId="formBankAccountIBAN"
                        label={t("pages:profile.iban")}
                        placeholder={t("common:form.placeholders.enterData")}
                        error={validation.touched.iban && !validation.ibanValid
                            ? t("pages:profile.invalidIBAN")
                            : ""}
                        value={formData.iban}
                        onChange={(value) => handleChange('iban', value)}
                    />
                    <ProfileEditNode
                        controlId="formBankAccountName"
                        label={t("pages:profile.ibanOwner")}
                        placeholder={t("common:form.placeholders.enterData")}
                        error={validation.touched.ownerName && !formData.ownerName.trim()
                            ? t("common:form.errors.required")
                            : ""}
                        value={formData.ownerName}
                        onChange={(value) => handleChange('ownerName', value)}
                    />
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ChangeBankAccount;